import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { getAllLeaves, updateLeaveStatus } from "../../api/api";
import { useNavigate } from "react-router-dom";
import SuccessModal from "../../components/SuccessModal";

// ======================================================
// INJECT SPIN ANIMATION ONCE
// ======================================================

if (
  typeof document !== "undefined" &&
  !document.getElementById("__review-spin")
) {
  const s = document.createElement("style");
  s.id = "__review-spin";
  s.innerHTML = `
    @keyframes spin {
      0% {
        transform: rotate(0deg);
      }

      100% {
        transform: rotate(360deg);
      }
    }
  `;

  document.head.appendChild(s);
}

// ======================================================
// RESPONSIVE HOOK
// ======================================================

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.innerWidth < 768
  );

  useEffect(() => {
    const handler = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handler);

    return () => {
      window.removeEventListener("resize", handler);
    };
  }, []);

  return isMobile;
}

// ======================================================
// DATE HELPERS
// ======================================================

const formatDate = (date) => {
  if (!date) return "-";

  const parsedDate = new Date(date);

  if (Number.isNaN(parsedDate.getTime())) {
    return "-";
  }

  return parsedDate
    .toLocaleDateString("en-GB")
    .replace(/\//g, "-");
};

const formatDateTime = (date) => {
  if (!date) return "-";

  const parsedDate = new Date(date);

  if (Number.isNaN(parsedDate.getTime())) {
    return "-";
  }

  return parsedDate
    .toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
    .replace(/\//g, "-")
    .replace(",", "");
};

const getReasonText = (reason) => {
  if (!reason) return "-";

  if (typeof reason === "object") {
    try {
      return JSON.stringify(reason);
    } catch {
      return "-";
    }
  }

  return String(reason);
};

// ======================================================
// MAIN COMPONENT
// ======================================================

export default function ReviewLeave() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);

  const [filterType, setFilterType] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [comment, setComment] = useState("");

  const [successOpen, setSuccessOpen] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");

  const [confirming, setConfirming] = useState(false);

  const itemsPerPage = 10;

  // ======================================================
  // FETCH LEAVES
  // ======================================================

  const fetchLeaves = async () => {
    try {
      setLoading(true);

      const data = await getAllLeaves();

      setLeaves(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(
        "[ReviewLeave] fetchLeaves error:",
        error
      );

      setLeaves([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaves();
  }, []);

  // ======================================================
  // STATUS STYLE
  // ======================================================

  const getStatusStyle = (status) => {
    switch (status?.toLowerCase()) {
      case "approved":
        return styles.approved;

      case "rejected":
        return styles.rejected;

      case "pending":
        return styles.pending;

      case "withdrawn":
        return styles.withdrawn;

      default:
        return styles.defaultStatus;
    }
  };

  // ======================================================
  // LEAVE TYPE
  // ======================================================

  const getLeaveTypeName = (leave) =>
    leave?.leave_type_name ||
    leave?.leave_type?.name ||
    leave?.leave_type ||
    "-";

  // ======================================================
  // FILTERS
  // ======================================================

  const filteredLeaves = leaves.filter((leave) => {
    const leaveTypeName = getLeaveTypeName(leave);

    const matchType =
      !filterType ||
      filterType === "All" ||
      leaveTypeName === filterType;

    const leaveFrom = leave.from_date
      ? new Date(leave.from_date)
      : null;

    const filterFromDate = fromDate
      ? new Date(`${fromDate}T00:00:00`)
      : null;

    const filterToDate = toDate
      ? new Date(`${toDate}T23:59:59`)
      : null;

    const matchFrom =
      !filterFromDate ||
      (leaveFrom &&
        !Number.isNaN(leaveFrom.getTime()) &&
        leaveFrom >= filterFromDate);

    const matchTo =
      !filterToDate ||
      (leaveFrom &&
        !Number.isNaN(leaveFrom.getTime()) &&
        leaveFrom <= filterToDate);

    return matchType && matchFrom && matchTo;
  });

  // ======================================================
  // PAGINATION
  // ======================================================

  const totalPages =
    Math.ceil(filteredLeaves.length / itemsPerPage) || 1;

  const safeCurrentPage = Math.min(
    currentPage,
    totalPages
  );

  const paginatedLeaves = filteredLeaves.slice(
    (safeCurrentPage - 1) * itemsPerPage,
    safeCurrentPage * itemsPerPage
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [filterType, fromDate, toDate]);

  // ======================================================
  // OPEN CONFIRM MODAL
  // ======================================================

  const openConfirm = (id, status) => {
    setSelectedAction({
      id,
      status,
    });

    setComment("");
    setConfirmOpen(true);
  };

  // ======================================================
  // CLOSE CONFIRM MODAL
  // ======================================================

  const closeConfirm = () => {
    if (confirming) return;

    setConfirmOpen(false);
    setSelectedAction(null);
    setComment("");
  };

  // ======================================================
  // CONFIRM STATUS UPDATE
  // ======================================================

  const handleConfirm = async () => {
    if (!selectedAction || confirming) {
      return;
    }

    if (
      selectedAction.status === "rejected" &&
      !comment.trim()
    ) {
      alert("Comment is required for rejection");
      return;
    }

    setConfirming(true);

    const actionStatus = selectedAction.status;

    try {
      const result = await updateLeaveStatus(
        selectedAction.id,
        actionStatus,
        actionStatus === "approved"
          ? ""
          : comment.trim()
      );

      console.log(
        "[ReviewLeave] updateLeaveStatus success:",
        result
      );

      setConfirmOpen(false);
      setSelectedAction(null);
      setComment("");

      setSuccessMsg(
        actionStatus === "approved"
          ? "Leave approved successfully"
          : "Leave rejected successfully"
      );

      setSuccessOpen(true);

      await fetchLeaves();
    } catch (err) {
      console.error(
        "[ReviewLeave] updateLeaveStatus FAILED:",
        err
      );

      const message =
        err?.error ||
        err?.response?.data?.message ||
        err?.response?.data?.errors?.[0] ||
        err?.message ||
        "Something went wrong while updating leave status";

      alert(message);
    } finally {
      setConfirming(false);
    }
  };

  // ======================================================
  // CONFIRM MODAL
  // ======================================================

  const confirmModal =
    confirmOpen &&
    typeof document !== "undefined" &&
    selectedAction &&
    createPortal(
      <div
        style={styles.modalOverlay}
        onClick={closeConfirm}
      >
        <div
          style={{
            ...styles.modal,
            width: isMobile
              ? "calc(100vw - 32px)"
              : "420px",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <h3 style={styles.modalTitle}>
            {selectedAction.status === "approved"
              ? "Approve Leave"
              : "Reject Leave"}
          </h3>

          {selectedAction.status === "approved" && (
            <div style={styles.confirmMessage}>
              Are you sure you want to approve this leave
              request?
            </div>
          )}

          {selectedAction.status === "rejected" && (
            <>
              <div style={styles.confirmMessage}>
                Please provide a reason for rejecting this
                leave request.
              </div>

              <textarea
                placeholder="Write rejection reason..."
                value={comment}
                onChange={(e) =>
                  setComment(e.target.value)
                }
                disabled={confirming}
                style={styles.textarea}
              />
            </>
          )}

          <div style={styles.modalActions}>
            <button
              type="button"
              style={{
                ...styles.cancelBtn,
                opacity: confirming ? 0.6 : 1,
                cursor: confirming
                  ? "not-allowed"
                  : "pointer",
              }}
              disabled={confirming}
              onClick={closeConfirm}
            >
              Cancel
            </button>

            <button
              type="button"
              style={{
                ...styles.confirmBtn,
                ...(selectedAction.status === "rejected"
                  ? styles.confirmRejectBtn
                  : {}),
                opacity: confirming ? 0.6 : 1,
                cursor: confirming
                  ? "not-allowed"
                  : "pointer",
              }}
              disabled={confirming}
              onClick={handleConfirm}
            >
              {confirming
                ? "Please wait..."
                : selectedAction.status === "approved"
                ? "Approve"
                : "Reject"}
            </button>
          </div>
        </div>
      </div>,
      document.body
    );

  // ======================================================
  // UI
  // ======================================================

  return (
    <div
      style={{
        ...styles.container,
        padding: isMobile ? "14px" : "28px",
      }}
    >
      {/* ====================================================== */}
      {/* HEADER */}
      {/* ====================================================== */}

      <div style={styles.pageHeader}>
        <h1
          style={{
            ...styles.heading,
            fontSize: isMobile ? "22px" : "30px",
          }}
        >
          Leave Review Dashboard
        </h1>

        <p style={styles.subHeading}>
          Review, approve and manage employee leave requests
        </p>
      </div>

      {/* ====================================================== */}
      {/* FILTERS */}
      {/* ====================================================== */}

      <div
        style={{
          ...styles.filterCard,
          padding: isMobile ? "14px" : "22px",
        }}
      >
        <div
          style={{
            ...styles.filterRow,
            flexDirection: isMobile
              ? "column"
              : "row",
            gap: isMobile ? "10px" : "18px",
          }}
        >
          <div style={styles.inputGroup}>
            <label style={styles.label}>
              From Date
            </label>

            <input
              type="date"
              value={fromDate}
              onChange={(e) =>
                setFromDate(e.target.value)
              }
              style={{
                ...styles.input,
                width: isMobile
                  ? "100%"
                  : "auto",
                boxSizing: "border-box",
              }}
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>
              To Date
            </label>

            <input
              type="date"
              value={toDate}
              onChange={(e) =>
                setToDate(e.target.value)
              }
              style={{
                ...styles.input,
                width: isMobile
                  ? "100%"
                  : "auto",
                boxSizing: "border-box",
              }}
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>
              Leave Type
            </label>

            <select
              value={filterType}
              onChange={(e) =>
                setFilterType(e.target.value)
              }
              style={{
                ...styles.select,
                width: isMobile
                  ? "100%"
                  : "auto",
                boxSizing: "border-box",
              }}
            >
              <option value="">
                Select Leave Type
              </option>

              <option value="All">
                All
              </option>

              <option value="Loss Of Pay">
                Loss Of Pay
              </option>

              <option value="Planned Leave">
                Planned Leave
              </option>

              <option value="Unplanned Leave">
                Unplanned Leave
              </option>

              <option value="Sick Leave">
                Sick Leave
              </option>
            </select>
          </div>
        </div>
      </div>

      {/* ====================================================== */}
      {/* CONTENT */}
      {/* ====================================================== */}

      {loading ? (
        <div style={styles.loaderWrapper}>
          <div style={styles.loader} />

          <p style={styles.loaderText}>
            Loading leave requests...
          </p>
        </div>
      ) : paginatedLeaves.length === 0 ? (
        <div style={styles.emptyState}>
          No leave requests found
        </div>
      ) : isMobile ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "12px",
          }}
        >
          {paginatedLeaves.map((leave) => (
            <ReviewCard
              key={leave.id}
              leave={leave}
              getStatusStyle={getStatusStyle}
              getLeaveTypeName={getLeaveTypeName}
              onNavigate={() =>
                navigate(
                  `/manager/review/${leave.id}`
                )
              }
              onApprove={() =>
                openConfirm(
                  leave.id,
                  "approved"
                )
              }
              onReject={() =>
                openConfirm(
                  leave.id,
                  "rejected"
                )
              }
            />
          ))}
        </div>
      ) : (
        <div style={styles.tableCard}>
          <div style={styles.tableWrapper}>
            <div style={styles.tableHeader}>
              <div>Name</div>
              <div>Type</div>
              <div>From</div>
              <div>To</div>
              <div>Reason</div>
              <div>Status</div>
              <div>Action</div>
            </div>

            {paginatedLeaves.map((leave) => (
              <div
                key={leave.id}
                style={styles.row}
                onClick={() =>
                  navigate(
                    `/manager/review/${leave.id}`
                  )
                }
              >
                <div style={styles.cell}>
                  {leave.user_name ||
                    leave.employee_name ||
                    "Employee"}
                </div>

                <div style={styles.cell}>
                  {getLeaveTypeName(leave)}
                </div>

                <div style={styles.cell}>
                  {formatDate(
                    leave.from_date
                  )}
                </div>

                <div style={styles.cell}>
                  {formatDate(
                    leave.to_date
                  )}
                </div>

                <div style={styles.reasonCell}>
                  {getReasonText(
                    leave.reason
                  )}
                </div>

                <div style={styles.cell}>
                  <span
                    style={{
                      ...styles.statusBadge,
                      ...getStatusStyle(
                        leave.status
                      ),
                    }}
                  >
                    {leave.status || "-"}
                  </span>
                </div>

                <div
                  style={styles.actions}
                  onClick={(e) =>
                    e.stopPropagation()
                  }
                >
                  {leave.status?.toLowerCase() ===
                  "pending" ? (
                    <>
                      <button
                        type="button"
                        style={
                          styles.approveBtn
                        }
                        onClick={(e) => {
                          e.stopPropagation();

                          openConfirm(
                            leave.id,
                            "approved"
                          );
                        }}
                      >
                        Approve
                      </button>

                      <button
                        type="button"
                        style={
                          styles.rejectBtn
                        }
                        onClick={(e) => {
                          e.stopPropagation();

                          openConfirm(
                            leave.id,
                            "rejected"
                          );
                        }}
                      >
                        Reject
                      </button>
                    </>
                  ) : (
                    <span
                      style={
                        styles.completedText
                      }
                    >
                      Completed
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ====================================================== */}
      {/* PAGINATION */}
      {/* ====================================================== */}

      {!loading &&
        filteredLeaves.length > 0 && (
          <div style={styles.pagination}>
            <button
              type="button"
              style={{
                ...styles.paginationBtn,
                opacity:
                  safeCurrentPage === 1
                    ? 0.4
                    : 1,
              }}
              disabled={
                safeCurrentPage === 1
              }
              onClick={() =>
                setCurrentPage((page) =>
                  Math.max(1, page - 1)
                )
              }
            >
              ◀ {!isMobile && "Previous"}
            </button>

            <span style={styles.pageText}>
              {isMobile
                ? `${safeCurrentPage}/${totalPages}`
                : `Page ${safeCurrentPage} of ${totalPages}`}
            </span>

            <button
              type="button"
              style={{
                ...styles.paginationBtn,
                opacity:
                  safeCurrentPage ===
                  totalPages
                    ? 0.4
                    : 1,
              }}
              disabled={
                safeCurrentPage ===
                totalPages
              }
              onClick={() =>
                setCurrentPage((page) =>
                  Math.min(
                    totalPages,
                    page + 1
                  )
                )
              }
            >
              {!isMobile && "Next"} ▶
            </button>
          </div>
        )}

      {/* ====================================================== */}
      {/* MODALS */}
      {/* ====================================================== */}

      {confirmModal}

      <SuccessModal
        open={successOpen}
        message={successMsg}
        onClose={() =>
          setSuccessOpen(false)
        }
      />
    </div>
  );
}

// ======================================================
// MOBILE REVIEW CARD
// ======================================================

function ReviewCard({
  leave,
  getStatusStyle,
  getLeaveTypeName,
  onNavigate,
  onApprove,
  onReject,
}) {
  const actionTapped = useRef(false);

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: "14px",
        padding: "16px",
        boxShadow:
          "0 4px 14px rgba(0,0,0,0.05)",
        cursor: "pointer",
      }}
      onClick={() => {
        if (actionTapped.current) {
          actionTapped.current = false;
          return;
        }

        onNavigate();
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "10px",
          marginBottom: "10px",
        }}
      >
        <span
          style={{
            fontWeight: "700",
            fontSize: "14px",
            color: "#111827",
          }}
        >
          {leave.user_name ||
            leave.employee_name ||
            "Employee"}
        </span>

        <span
          style={{
            ...styles.statusBadge,
            ...getStatusStyle(
              leave.status
            ),
            fontSize: "11px",
            padding: "4px 10px",
          }}
        >
          {leave.status || "-"}
        </span>
      </div>

      <div
        style={{
          display: "flex",
          gap: "14px",
          marginBottom: "8px",
          flexWrap: "wrap",
        }}
      >
        <div>
          <div style={mL}>Type</div>

          <div style={mV}>
            {getLeaveTypeName(leave)}
          </div>
        </div>

        <div
          style={{
            borderLeft:
              "1px solid #e5e7eb",
          }}
        />

        <div>
          <div style={mL}>From</div>

          <div style={mV}>
            {formatDate(
              leave.from_date
            )}
          </div>
        </div>

        <div
          style={{
            borderLeft:
              "1px solid #e5e7eb",
          }}
        />

        <div>
          <div style={mL}>To</div>

          <div style={mV}>
            {formatDate(
              leave.to_date
            )}
          </div>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          gap: "14px",
          marginBottom: "10px",
          flexWrap: "wrap",
        }}
      >
        <div>
          <div style={mL}>Days</div>

          <div style={mV}>
            {leave.days ??
              leave.requested_days ??
              "-"}
          </div>
        </div>

        <div
          style={{
            borderLeft:
              "1px solid #e5e7eb",
          }}
        />

        <div>
          <div style={mL}>Category</div>

          <div style={mV}>
            {leave.leave_category ||
              "-"}
          </div>
        </div>
      </div>

      {leave.reason && (
        <div
          style={{
            borderTop:
              "1px solid #f3f4f6",
            paddingTop: "8px",
            marginBottom: "10px",
          }}
        >
          <div style={mL}>Reason</div>

          <div
            style={{
              ...mV,
              fontWeight: "400",
              color: "#4b5563",
              fontSize: "13px",
              wordBreak: "break-word",
            }}
          >
            {getReasonText(
              leave.reason
            )}
          </div>
        </div>
      )}

      {leave.status?.toLowerCase() ===
      "pending" ? (
        <div
          style={{
            display: "flex",
            gap: "8px",
            marginTop: "6px",
          }}
          onClick={(e) =>
            e.stopPropagation()
          }
        >
          <button
            type="button"
            style={{
              ...styles.approveBtn,
              flex: 1,
              padding: "10px",
            }}
            onClick={(e) => {
              e.stopPropagation();

              actionTapped.current = true;

              onApprove();
            }}
          >
            Approve
          </button>

          <button
            type="button"
            style={{
              ...styles.rejectBtn,
              flex: 1,
              padding: "10px",
            }}
            onClick={(e) => {
              e.stopPropagation();

              actionTapped.current = true;

              onReject();
            }}
          >
            Reject
          </button>
        </div>
      ) : (
        <div style={{ marginTop: "6px" }}>
          <span
            style={styles.completedText}
          >
            Completed
          </span>
        </div>
      )}
    </div>
  );
}

// ======================================================
// MOBILE LABEL/VALUE STYLES
// ======================================================

const mL = {
  fontSize: "10px",
  color: "#94a3b8",
  fontWeight: "600",
  textTransform: "uppercase",
  letterSpacing: "0.4px",
  marginBottom: "2px",
};

const mV = {
  fontSize: "13px",
  color: "#111827",
  fontWeight: "600",
};

// ======================================================
// STYLES
// ======================================================

const styles = {
  container: {
    minHeight: "100vh",
    background:
      "linear-gradient(to bottom right,#f3f7fb,#eef2f7)",
    fontFamily:
      "'Inter',Arial,sans-serif",
  },

  pageHeader: {
    marginBottom: "20px",
  },

  heading: {
    margin: 0,
    fontWeight: "800",
    color: "#111827",
  },

  subHeading: {
    marginTop: "6px",
    color: "#6b7280",
    fontSize: "14px",
  },

  filterCard: {
    background: "#fff",
    borderRadius: "16px",
    marginBottom: "20px",
    boxShadow:
      "0 8px 25px rgba(0,0,0,0.05)",
    border: "1px solid #e5e7eb",
  },

  filterRow: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "flex-end",
  },

  inputGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    width: "100%",
    flex: 1,
  },

  label: {
    fontSize: "13px",
    fontWeight: "600",
    color: "#374151",
  },

  input: {
    padding: "11px 14px",
    borderRadius: "10px",
    border: "1px solid #d1d5db",
    fontSize: "14px",
    outline: "none",
    background: "#fff",
    color: "#111827",
  },

  select: {
    padding: "11px 14px",
    borderRadius: "10px",
    border: "1px solid #d1d5db",
    fontSize: "14px",
    outline: "none",
    background: "#fff",
    color: "#111827",
  },

  tableCard: {
    background: "#fff",
    borderRadius: "16px",
    overflow: "hidden",
    boxShadow:
      "0 10px 30px rgba(0,0,0,0.05)",
    border: "1px solid #e5e7eb",
  },

  tableWrapper: {
    overflowX: "auto",
  },

  tableHeader: {
    display: "grid",
    gridTemplateColumns:
      "180px 170px 130px 130px 220px 130px 220px",
    padding: "18px 20px",
    background: "#f9fafb",
    borderBottom:
      "1px solid #e5e7eb",
    fontWeight: "700",
    color: "#374151",
    minWidth: "1180px",
  },

  row: {
    display: "grid",
    gridTemplateColumns:
      "180px 170px 130px 130px 220px 130px 220px",
    padding: "16px 20px",
    borderBottom:
      "1px solid #f1f5f9",
    alignItems: "center",
    minWidth: "1180px",
    cursor: "pointer",
  },

  cell: {
    fontSize: "14px",
    color: "#374151",
  },

  reasonCell: {
    fontSize: "14px",
    color: "#374151",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  statusBadge: {
    padding: "6px 12px",
    borderRadius: "999px",
    fontSize: "12px",
    fontWeight: "600",
    textTransform: "capitalize",
    display: "inline-block",
  },

  approved: {
    background: "#dcfce7",
    color: "#166534",
  },

  rejected: {
    background: "#fee2e2",
    color: "#991b1b",
  },

  pending: {
    background: "#fef3c7",
    color: "#92400e",
  },

  withdrawn: {
    background: "#ede9fe",
    color: "#5b21b6",
  },

  defaultStatus: {
    background: "#e5e7eb",
    color: "#374151",
  },

  actions: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
  },

  approveBtn: {
    background:
      "linear-gradient(135deg,#22c55e,#16a34a)",
    color: "#fff",
    border: "none",
    padding: "9px 14px",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: "600",
    fontSize: "13px",
  },

  rejectBtn: {
    background:
      "linear-gradient(135deg,#ef4444,#dc2626)",
    color: "#fff",
    border: "none",
    padding: "9px 14px",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: "600",
    fontSize: "13px",
  },

  completedText: {
    color: "#6b7280",
    fontSize: "13px",
    fontWeight: "600",
  },

  emptyState: {
    padding: "60px",
    textAlign: "center",
    color: "#6b7280",
    fontSize: "15px",
    background: "#fff",
    borderRadius: "16px",
    border: "1px solid #e5e7eb",
  },

  loaderWrapper: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    padding: "80px 0",
  },

  loader: {
    width: "50px",
    height: "50px",
    border: "5px solid #dbeafe",
    borderTop: "5px solid #2563eb",
    borderRadius: "50%",
    animation:
      "spin 1s linear infinite",
  },

  loaderText: {
    marginTop: "14px",
    color: "#6b7280",
    fontWeight: "500",
  },

  pagination: {
    marginTop: "20px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "14px",
  },

  paginationBtn: {
    background: "#fff",
    border:
      "1px solid #d1d5db",
    padding: "10px 16px",
    borderRadius: "10px",
    cursor: "pointer",
    fontWeight: "600",
  },

  pageText: {
    fontWeight: "600",
    color: "#374151",
  },

  modalOverlay: {
    position: "fixed",
    inset: 0,
    background:
      "rgba(0,0,0,0.45)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 9999,
    padding: "16px",
    boxSizing: "border-box",
  },

  modal: {
    background: "#fff",
    borderRadius: "18px",
    padding: "24px",
    boxShadow:
      "0 20px 45px rgba(0,0,0,0.2)",
    boxSizing: "border-box",
    maxHeight: "90vh",
    overflowY: "auto",
  },

  modalTitle: {
    margin: "0 0 14px",
    fontSize: "20px",
    fontWeight: "700",
    color: "#111827",
  },

  confirmMessage: {
    marginBottom: "14px",
    color: "#4b5563",
    fontSize: "14px",
    lineHeight: "20px",
  },

  textarea: {
    width: "100%",
    minHeight: "120px",
    padding: "14px",
    borderRadius: "12px",
    border:
      "1px solid #d1d5db",
    fontSize: "14px",
    resize: "none",
    outline: "none",
    boxSizing: "border-box",
    fontFamily:
      "'Inter',Arial,sans-serif",
  },

  modalActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
    marginTop: "16px",
  },

  cancelBtn: {
    background: "#f3f4f6",
    border: "none",
    padding: "10px 18px",
    borderRadius: "10px",
    cursor: "pointer",
    fontWeight: "600",
    color: "#374151",
  },

  confirmBtn: {
    background:
      "linear-gradient(135deg,#2563eb,#1d4ed8)",
    color: "#fff",
    border: "none",
    padding: "10px 18px",
    borderRadius: "10px",
    fontWeight: "600",
    cursor: "pointer",
  },

  confirmRejectBtn: {
    background:
      "linear-gradient(135deg,#ef4444,#dc2626)",
  },
};
